/**
 * PurchaseOrdersPage — Lieferanten-Bestellwesen.
 *
 * Workflow: draft → sent → partial → received  (+ cancelled terminal)
 *
 * Features:
 *  - Liste mit Status-Filter
 *  - Auto-Vorschläge basierend auf Mindestbestand (gruppiert nach Lieferant)
 *  - Manuelle Bestell-Erstellung
 *  - Wareneingangs-Modal mit Soll/Ist-Abgleich (Teil-Lieferungen erlaubt)
 *  - PDF-Download je Bestellung
 *
 * Aufruf: /dealer/purchase-orders
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Plus, Truck, X, Trash2, FileText, Download, Check,
    Package, ArrowRight, Send, Sparkles, Edit2, ClipboardCheck, AlertTriangle, Loader2,
} from "lucide-react";

import api from "../lib/api";
import { previewPdfResponse } from "../lib/pdfPreview";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";

const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const fmtNum = (v) => Number(v || 0).toLocaleString("de-DE", { maximumFractionDigits: 2 });
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("de-DE") : "—";

const STATUS_META = {
    draft: { label: "Entwurf", color: "bg-gray-100 text-gray-700" },
    sent: { label: "Versendet", color: "bg-blue-100 text-blue-800" },
    partial: { label: "Teil-Eingang", color: "bg-amber-100 text-amber-900" },
    received: { label: "Komplett", color: "bg-green-100 text-green-800" },
    cancelled: { label: "Storniert", color: "bg-red-100 text-red-700" },
};

export default function PurchaseOrdersPage({ embedded = false }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [articles, setArticles] = useState([]);
    const [suggestions, setSuggestions] = useState({ groups: [], total_articles: 0 });
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState("");
    const [createPrefill, setCreatePrefill] = useState(null); // null|{}|prefill
    const [aiBusy, setAiBusy] = useState(null);
    const [receiveTarget, setReceiveTarget] = useState(null);
    const [sendTarget, setSendTarget] = useState(null);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [po, sup, art, sugg] = await Promise.all([
                api.get("/purchase-orders"),
                api.get("/suppliers"),
                api.get("/articles"),
                api.get("/purchase-orders/suggestions"),
            ]);
            setOrders(po.data || []);
            setSuppliers(sup.data || []);
            setArticles(art.data || []);
            setSuggestions(sugg.data || { groups: [], total_articles: 0 });
            return sup.data || [];
        } catch (e) {
            toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user === false) navigate("/login");
        if (user && user.role !== "b2b" && user.role !== "admin" && user.role !== "zweiradverkaeufer") navigate("/");
        if (user) {
            // „Schnell-anlegen & bestellen"-Shortcut vom Partner: ?supplier=<id>
            loadAll().then((sup) => {
                const sid = new URLSearchParams(window.location.search).get("supplier");
                if (sid && (sup || []).some((s) => s.id === sid)) {
                    setCreatePrefill({ supplier_id: sid });
                    navigate("/dealer/purchase-orders", { replace: true });
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const sendOrder = async (id, payload) => {
        try {
            const res = await api.post(`/purchase-orders/${id}/send`, payload || {});
            const tag = res.data?.email?.mocked ? " (Mock)" : "";
            const target = res.data?.email?.to ? ` an ${res.data.email.to}` : "";
            toast.success(`Bestellung versendet${target}${tag}`);
            setSendTarget(null);
            loadAll();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    };

    const cancelOrder = async (id) => {
        if (!window.confirm("Bestellung stornieren?")) return;
        try {
            await api.post(`/purchase-orders/${id}/cancel`);
            toast.success("Bestellung storniert");
            loadAll();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    };

    const removeOrder = async (id) => {
        if (!window.confirm("Entwurf löschen?")) return;
        try {
            await api.delete(`/purchase-orders/${id}`);
            toast.success("Entwurf gelöscht");
            loadAll();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
    };

    const downloadPdf = async (id, number) => {
        try {
            const res = await api.get(`/purchase-orders/${id}/pdf`, { responseType: "blob" });
            previewPdfResponse(res, `${number || id}.pdf`, { docType: "bestellung" });
        } catch { toast.error("PDF-Download fehlgeschlagen"); }
    };

    // Aug-2026 (User-Wunsch): Bestellung als Niemann+Frey-Bestelldatei exportieren
    const downloadNfFile = async (id, number) => {
        try {
            const res = await api.get(`/purchase-orders/${id}/nf-export`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url; a.download = `NF_Bestellung_${number || id}.csv`; a.click();
            URL.revokeObjectURL(url);
            toast.success("N+F-Bestelldatei erstellt — im Händlerportal unter ‚Bestellen' hochladen");
        } catch (e) { toast.error(e.response?.data?.detail || "Export fehlgeschlagen"); }
    };

    // Aug-2026 (User-Wunsch, Stufe 2): Positionen direkt in den N+F-Warenkorb laden
    const [nfUploadBusy, setNfUploadBusy] = useState(null);
    const nfUpload = async (id) => {
        setNfUploadBusy(id);
        try {
            const r = await api.post(`/purchase-orders/${id}/nf-upload`);
            const d = r.data || {};
            if ((d.failed || []).length > 0) {
                toast.warning(`${d.uploaded}/${d.total} Positionen im N+F-Warenkorb — nicht erkannt: ${d.failed.join(", ")}`, { duration: 9000 });
            } else {
                toast.success(`${d.uploaded} Position(en) in den N+F-Warenkorb übertragen (${d.cart_net_sum || ""}) — bitte im Portal prüfen & absenden`, { duration: 8000 });
            }
            loadAll();
        } catch (e) {
            toast.error(String(e.response?.data?.detail || "Übertragung fehlgeschlagen"), { duration: 9000 });
        }
        setNfUploadBusy(null);
    };

    // Aug-2026 (User-Wunsch): N+F-Lieferbarkeit je Position prüfen (vor dem Warenkorb)
    const [nfAvailBusy, setNfAvailBusy] = useState(null);
    const [nfAvailResults, setNfAvailResults] = useState({});
    const nfAvailability = async (id) => {
        setNfAvailBusy(id);
        try {
            const r = await api.post(`/purchase-orders/${id}/nf-availability`);
            setNfAvailResults((m) => ({ ...m, [id]: r.data?.items || [] }));
        } catch (e) {
            toast.error(String(e.response?.data?.detail || "Verfügbarkeits-Prüfung fehlgeschlagen"), { duration: 8000 });
        }
        setNfAvailBusy(null);
    };

    const createFromSuggestion = (group) => {
        setCreatePrefill({
            supplier_id: group.supplier_id || "",
            items: group.items.map((it) => ({
                article_id: it.article_id,
                sku: it.sku,
                name: it.name,
                quantity: it.suggested_qty,
                unit_price_net: it.unit_price_net,
                vat_rate: it.vat_rate,
                discount_percent: 0,
            })),
        });
    };

    // KI-Mengenvorschlag (Verkaufshistorie) für eine Lieferanten-Gruppe.
    const aiSuggestGroup = async (gi, group) => {
        setAiBusy(gi);
        try {
            const ids = group.items.map((it) => it.article_id);
            const r = await api.post("/purchase-orders/ai-suggest", { article_ids: ids });
            const byId = Object.fromEntries((r.data?.items || []).map((x) => [x.article_id, x]));
            setSuggestions((prev) => {
                const groups = prev.groups.map((g, idx) => idx !== gi ? g : ({
                    ...g,
                    items: g.items.map((it) => {
                        const ai = byId[it.article_id];
                        return ai ? { ...it, suggested_qty: ai.ai_suggested_qty, ai_reason: ai.reason, ai: true } : it;
                    }),
                }));
                return { ...prev, groups };
            });
            toast.success(r.data?.ai_used ? "KI-Mengen aus Verkaufshistorie übernommen" : "Mengen (Heuristik) aktualisiert");
        } catch (e) {
            toast.error(e.response?.data?.detail || "KI-Vorschlag fehlgeschlagen");
        } finally {
            setAiBusy(null);
        }
    };

    if (!user) return <div className="p-8">Lade…</div>;
    const supById = Object.fromEntries(suppliers.map((s) => [s.id, s]));
    const visibleOrders = filterStatus ? orders.filter((o) => o.status === filterStatus) : orders;

    // Aug-2026 (User-Wunsch): Seite auch als WMS-Tab „Bestellungen" eingebettet —
    // dann ohne eigenen dunklen Header und ohne max-w-Wrapper (WMS liefert den Rahmen).
    const wrap = embedded ? "" : "max-w-7xl mx-auto px-4 md:px-8";
    return (
        <div className={embedded ? "" : "min-h-screen bg-gray-50"} data-testid="po-page">
            {!embedded && (
            <div className="bg-[#111827] text-white">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                    <div className="flex items-end justify-between gap-4 flex-wrap">
                        <div>
                            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight">Lieferanten-Bestellungen</h1>
                            <p className="text-sm text-gray-400 mt-2 truncate max-w-full">
                                Bedarf → Bestellung → Wareneingang → Lager. Auto-Vorschläge auf Basis Mindestbestand.
                            </p>
                        </div>
                    </div>
                    {/* Zeile 1: Navigation — Dashboard ganz links, Rest rechtsbündig */}
                    <div className="mt-4 flex flex-wrap gap-2 items-center" data-testid="po-nav-row">
                        <Link to="/dashboard" className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm font-bold" data-testid="back-to-dashboard">← Dashboard</Link>
                        <div className="flex-1" />
                        <Link to="/dealer/wms" className="text-xs uppercase tracking-wider text-gray-300 hover:text-white border border-gray-600 px-3 py-2 rounded-sm" data-testid="link-wms">← Warenwirtschaft</Link>
                    </div>
                    {/* Zeile 2: Funktionen dieser Seite — rechtsbündig */}
                    <div className="mt-3 flex flex-wrap gap-2 justify-end" data-testid="po-action-row">
                        <Button onClick={() => setCreatePrefill({})} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm" data-testid="po-create-btn">
                            <Plus className="w-4 h-4 mr-1.5" /> Neue Bestellung
                        </Button>
                    </div>
                </div>
            </div>
            )}
            {embedded && (
                <div className="flex flex-wrap items-center justify-between gap-2" data-testid="po-embedded-header">
                    <div>
                        <div className="font-display font-black text-lg">Lieferanten-Bestellungen</div>
                        <p className="text-xs text-gray-500">Bedarf → Bestellung → Wareneingang → Lager. Auto-Vorschläge auf Basis Mindestbestand.</p>
                    </div>
                    <Button onClick={() => setCreatePrefill({})} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm" data-testid="po-create-btn">
                        <Plus className="w-4 h-4 mr-1.5" /> Neue Bestellung
                    </Button>
                </div>
            )}

            {/* Auto-Vorschläge */}
            {suggestions.total_articles > 0 && (
                <div className={`${wrap} mt-6`}>
                    <div className="bg-amber-50 border-2 border-amber-300 rounded-sm p-4" data-testid="po-suggestions">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5 text-amber-700" />
                            <h2 className="font-display font-black text-lg">Bestell-Vorschläge ({suggestions.total_articles} Artikel unter Mindestbestand)</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {suggestions.groups.map((g, gi) => (
                                <div key={gi} className="bg-white border border-amber-200 rounded-sm p-3" data-testid={`po-sug-group-${gi}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="font-bold text-sm flex items-center gap-1.5">
                                            <Truck className="w-3.5 h-3.5 text-gray-500" />
                                            {g.supplier_name || "Ohne Lieferant"}
                                        </div>
                                        <Badge className="bg-amber-200 text-amber-900 text-[9px] font-bold uppercase rounded-sm">{g.items.length}</Badge>
                                    </div>
                                    <ul className="text-[11px] text-gray-700 space-y-0.5 mb-2 max-h-32 overflow-y-auto">
                                        {g.items.slice(0, 5).map((it) => (
                                            <li key={it.article_id}>
                                                <span className="font-mono-num text-gray-500">{it.sku}</span> {it.name}
                                                <div className="text-[10px] text-gray-500" title={it.ai_reason || ""}>
                                                    Bestand {fmtNum(it.stock_qty)} ≤ Min {fmtNum(it.min_stock)} → bestellen <strong>{fmtNum(it.suggested_qty)}</strong>
                                                    {it.ai && <span className="ml-1 inline-flex items-center gap-0.5 text-indigo-600 font-bold" data-testid={`po-sug-ai-${it.article_id}`}><Sparkles className="w-2.5 h-2.5" />KI</span>}
                                                </div>
                                                {it.ai && it.ai_reason && <div className="text-[9px] text-indigo-500 italic truncate">{it.ai_reason}</div>}
                                            </li>
                                        ))}
                                        {g.items.length > 5 && <li className="text-[10px] text-gray-400">… +{g.items.length - 5} weitere</li>}
                                    </ul>
                                    <div className="flex gap-1.5">
                                        <Button size="sm" variant="outline" onClick={() => aiSuggestGroup(gi, g)} disabled={aiBusy === gi} className="flex-1 rounded-sm text-[11px] font-bold border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid={`po-sug-ai-btn-${gi}`}>
                                            {aiBusy === gi ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5 mr-1" /> KI-Mengen</>}
                                        </Button>
                                        <Button size="sm" onClick={() => createFromSuggestion(g)} className="flex-1 rounded-sm bg-[#111827] hover:bg-black text-white text-[11px] font-bold" data-testid={`po-sug-create-${gi}`}>
                                            <Plus className="w-3.5 h-3.5 mr-1" /> Bestellung
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Status filter */}
            <div className={`${wrap} mt-6`}>
                <div className="flex gap-1.5 flex-wrap" data-testid="po-filter-chips">
                    {[{ k: "", l: "Alle" }, ...Object.entries(STATUS_META).map(([k, m]) => ({ k, l: m.label }))].map((f) => {
                        const count = f.k ? orders.filter((o) => o.status === f.k).length : orders.length;
                        return (
                            <button
                                key={f.k || "all"}
                                type="button"
                                onClick={() => setFilterStatus(f.k)}
                                className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border transition-colors inline-flex items-center gap-1.5 ${filterStatus === f.k ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                                data-testid={`po-filter-${f.k || "all"}`}
                            >
                                {f.l}
                                <span className={`text-[10px] px-1.5 rounded-sm ${filterStatus === f.k ? "bg-white/20" : "bg-gray-100"}`}>{count}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Orders list */}
            <div className={`${wrap} py-6`}>
                {loading ? (
                    <div className="text-center py-12 text-gray-500">Lade…</div>
                ) : visibleOrders.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-sm p-12 text-center" data-testid="po-empty">
                        <Truck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <div className="font-display font-black text-lg">Keine Bestellungen{filterStatus ? " in diesem Status" : ""}</div>
                        <p className="text-sm text-gray-500 mt-1">{filterStatus ? "Filter ändern oder neue Bestellung anlegen." : "Erstellen Sie Ihre erste Bestellung oder folgen Sie den Bestell-Vorschlägen."}</p>
                    </div>
                ) : (
                    <div className="space-y-2" data-testid="po-list">
                        {visibleOrders.map((o) => (
                            <POCard
                                key={o.id}
                                order={o}
                                supplier={supById[o.supplier_id]}
                                onSend={() => setSendTarget(o)}
                                onCancel={() => cancelOrder(o.id)}
                                onDelete={() => removeOrder(o.id)}
                                onReceive={() => setReceiveTarget(o)}
                                onPdf={() => downloadPdf(o.id, o.order_number)}
                                onNfExport={() => downloadNfFile(o.id, o.order_number)}
                                onNfUpload={() => nfUpload(o.id)}
                                nfUploadBusy={nfUploadBusy === o.id}
                                onNfAvail={() => nfAvailability(o.id)}
                                nfAvailBusy={nfAvailBusy === o.id}
                                nfAvail={nfAvailResults[o.id]}
                                onEdit={() => setCreatePrefill({ ...o, _edit: true })}
                            />
                        ))}
                    </div>
                )}
            </div>

            {createPrefill && (
                <POEditModal
                    prefill={createPrefill}
                    suppliers={suppliers}
                    articles={articles}
                    onClose={() => setCreatePrefill(null)}
                    onSaved={() => { setCreatePrefill(null); loadAll(); }}
                />
            )}
            {receiveTarget && (
                <ReceiveModal
                    order={receiveTarget}
                    onClose={() => setReceiveTarget(null)}
                    onReceived={() => { setReceiveTarget(null); loadAll(); }}
                />
            )}
            {sendTarget && (
                <SendOrderModal
                    order={sendTarget}
                    supplier={supById[sendTarget.supplier_id]}
                    onClose={() => setSendTarget(null)}
                    onSend={(payload) => sendOrder(sendTarget.id, payload)}
                />
            )}
        </div>
    );
}

function POCard({ order, supplier, onSend, onCancel, onDelete, onReceive, onPdf, onEdit, onNfExport, onNfUpload, nfUploadBusy, onNfAvail, nfAvailBusy, nfAvail }) {
    const meta = STATUS_META[order.status] || STATUS_META.draft;
    const itemCount = order.items?.length || 0;
    const totalOrdered = (order.items || []).reduce((s, it) => s + Number(it.quantity || 0), 0);
    const totalReceived = (order.items || []).reduce((s, it) => s + Number(it.received_qty || 0), 0);
    const recvPct = totalOrdered > 0 ? Math.round((totalReceived / totalOrdered) * 100) : 0;
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4 hover:border-[#111827] transition-colors" data-testid={`po-row-${order.id}`}>
            <div className="flex flex-wrap gap-3 items-start justify-between">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono-num font-black text-base">{order.order_number}</span>
                        <Badge className={`${meta.color} font-bold uppercase tracking-wider text-[10px] rounded-sm`} data-testid={`po-status-${order.id}`}>
                            {meta.label}
                        </Badge>
                        {(order.status === "sent" || order.status === "partial") && (
                            <Badge className="bg-amber-50 text-amber-800 border border-amber-200 text-[9px] font-bold rounded-sm uppercase">
                                {recvPct}% eingegangen
                            </Badge>
                        )}
                    </div>
                    <div className="mt-1.5 grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-0.5 text-sm">
                        <div className="text-gray-700"><span className="text-gray-400 text-[10px] uppercase tracking-wider block">Lieferant</span>{supplier?.name || "—"}</div>
                        <div className="text-gray-700"><span className="text-gray-400 text-[10px] uppercase tracking-wider block">Bestelldatum</span>{fmtDate(order.created_at)}</div>
                        <div className="text-gray-700"><span className="text-gray-400 text-[10px] uppercase tracking-wider block">Liefertermin</span>{fmtDate(order.expected_delivery_date)}</div>
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1">
                        {itemCount} {itemCount === 1 ? "Position" : "Positionen"} · {fmtNum(totalReceived)}/{fmtNum(totalOrdered)} erhalten
                    </div>
                    {nfAvail && (
                        <div className="mt-1 space-y-0.5" data-testid={`po-nf-avail-${order.id}`}>
                            {nfAvail.map((a) => (
                                <div key={a.sku} className={`text-[11px] font-semibold ${!a.found ? "text-red-700" : /sofort/i.test(a.status || "") ? "text-emerald-700" : "text-amber-700"}`}>
                                    {a.sku}: {a.found ? (a.status || "Status unbekannt") : "im N+F-Portal nicht gefunden"}
                                </div>
                            ))}
                        </div>
                    )}
                    {order.portal_upload?.at && (
                        <div className="text-[11px] text-emerald-800 mt-0.5 font-semibold" data-testid={`po-portal-status-${order.id}`}>
                            ✓ Im {order.portal_upload.portal}-Warenkorb: {new Date(order.portal_upload.at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} Uhr
                            {" · "}{order.portal_upload.uploaded}/{order.portal_upload.total} Pos.
                            {order.portal_upload.cart_net_sum ? ` · Warenkorb ${order.portal_upload.cart_net_sum}` : ""}
                        </div>
                    )}
                </div>
                <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">Netto</div>
                    <div className="font-mono-num font-black text-xl">{fmtEur(order.totals?.total_net)}</div>
                    <div className="text-[10px] text-gray-500">brutto {fmtEur(order.totals?.total_gross)}</div>
                </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100 flex gap-1.5 flex-wrap justify-end">
                <Button size="sm" variant="outline" onClick={onPdf} className="h-7 text-[11px] rounded-sm" data-testid={`po-pdf-${order.id}`}>
                    <Download className="w-3 h-3 mr-1" /> PDF
                </Button>
                {/niemann/i.test(supplier?.name || "") && (
                    <>
                        <Button size="sm" variant="outline" onClick={onNfExport}
                            className="h-7 text-[11px] rounded-sm border-emerald-400 text-emerald-800 hover:bg-emerald-50"
                            title="Bestelldatei (Artikelnummer;Menge;Kommission) für den Upload im N+F-Händlerportal unter ‚Bestellen'"
                            data-testid={`po-nf-export-${order.id}`}>
                            <Download className="w-3 h-3 mr-1" /> N+F-Datei
                        </Button>
                        <Button size="sm" variant="outline" onClick={onNfAvail} disabled={nfAvailBusy}
                            className="h-7 text-[11px] rounded-sm border-emerald-400 text-emerald-800 hover:bg-emerald-50"
                            title="N+F-Lieferbarkeit je Position prüfen (Login dieses Standorts) — ohne den Warenkorb zu befüllen"
                            data-testid={`po-nf-avail-btn-${order.id}`}>
                            <Check className="w-3 h-3 mr-1" /> {nfAvailBusy ? "Prüfe…" : "Verfügbarkeit"}
                        </Button>
                        <Button size="sm" onClick={onNfUpload} disabled={nfUploadBusy}
                            className="h-7 text-[11px] rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white font-bold"
                            title="Positionen direkt in den N+F-Warenkorb übertragen (Login dieses Standorts) — Bestellung wird NICHT abgesendet, finale Freigabe im Portal"
                            data-testid={`po-nf-upload-${order.id}`}>
                            <Send className="w-3 h-3 mr-1" /> {nfUploadBusy ? "Übertrage…" : order.nf_uploaded_at ? "Erneut in N+F-Warenkorb" : "In N+F-Warenkorb"}
                        </Button>
                    </>
                )}
                {order.status === "draft" && (
                    <>
                        <Button size="sm" variant="outline" onClick={onEdit} className="h-7 text-[11px] rounded-sm" data-testid={`po-edit-${order.id}`}>
                            <Edit2 className="w-3 h-3 mr-1" /> Bearbeiten
                        </Button>
                        <Button size="sm" variant="outline" onClick={onDelete} className="h-7 text-[11px] rounded-sm text-red-700" data-testid={`po-del-${order.id}`}>
                            <Trash2 className="w-3 h-3" />
                        </Button>
                        <Button size="sm" onClick={onSend} className="h-7 text-[11px] rounded-sm bg-[#111827] hover:bg-black text-white font-bold" data-testid={`po-send-${order.id}`}>
                            <Send className="w-3 h-3 mr-1" /> Versenden
                        </Button>
                    </>
                )}
                {(order.status === "sent" || order.status === "partial") && (
                    <>
                        <Button size="sm" onClick={onReceive} className="h-7 text-[11px] rounded-sm bg-green-600 hover:bg-green-700 text-white font-bold" data-testid={`po-receive-${order.id}`}>
                            <ClipboardCheck className="w-3 h-3 mr-1" /> Wareneingang
                        </Button>
                        <Button size="sm" variant="outline" onClick={onCancel} className="h-7 text-[11px] rounded-sm border-red-300 text-red-700" data-testid={`po-cancel-${order.id}`}>
                            <X className="w-3 h-3 mr-1" /> Stornieren
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}

function POEditModal({ prefill, suppliers, articles, onClose, onSaved }) {
    const isEdit = !!prefill?._edit;
    const [supplierId, setSupplierId] = useState(prefill?.supplier_id || "");
    const [items, setItems] = useState(prefill?.items?.length
        ? prefill.items.map((p) => ({ ...p }))
        : [{ article_id: "", quantity: 1, unit_price_net: 0, vat_rate: 19, discount_percent: 0 }]);
    const [expectedDate, setExpectedDate] = useState(prefill?.expected_delivery_date || "");
    const [notes, setNotes] = useState(prefill?.notes || "");
    const [busy, setBusy] = useState(false);

    const setItem = (i, k, v) => setItems((arr) => arr.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
    const addItem = () => setItems((arr) => [...arr, { article_id: "", quantity: 1, unit_price_net: 0, vat_rate: 19, discount_percent: 0 }]);
    const removeItem = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
    const linkArticle = (i, aid) => {
        const art = articles.find((a) => a.id === aid);
        if (!art) return;
        setItems((arr) => arr.map((x, idx) => idx === i ? {
            ...x,
            article_id: art.id, sku: art.sku, name: art.name,
            unit_price_net: Number(art.purchase_price_net || 0),
            vat_rate: Number(art.vat_rate || 19),
        } : x));
    };

    const totals = items.reduce((acc, it) => {
        const qty = Number(it.quantity) || 0;
        const net = qty * (Number(it.unit_price_net) || 0);
        const disc = net * ((Number(it.discount_percent) || 0) / 100);
        const ln = net - disc;
        return { net: acc.net + ln, vat: acc.vat + ln * (Number(it.vat_rate) || 0) / 100 };
    }, { net: 0, vat: 0 });
    const gross = totals.net + totals.vat;

    const submit = async (e) => {
        e.preventDefault();
        if (!supplierId) { toast.error("Lieferant wählen"); return; }
        if (items.some((i) => !i.article_id)) { toast.error("Jede Position braucht einen Artikel"); return; }
        setBusy(true);
        const payload = {
            supplier_id: supplierId,
            expected_delivery_date: expectedDate || null,
            notes: notes || null,
            items: items.map((i) => ({
                article_id: i.article_id,
                quantity: Number(i.quantity) || 0,
                unit_price_net: Number(i.unit_price_net) || 0,
                vat_rate: Number(i.vat_rate) || 19,
                discount_percent: Number(i.discount_percent) || 0,
            })),
        };
        try {
            if (isEdit) {
                await api.patch(`/purchase-orders/${prefill.id}`, payload);
            } else {
                await api.post("/purchase-orders", payload);
            }
            toast.success(isEdit ? "Bestellung aktualisiert" : "Bestellung angelegt");
            onSaved();
        } catch (e) {
            const d = e.response?.data?.detail;
            // Aug-2026 (User-Wunsch): Garantie-Teile → Hersteller-Pflicht.
            // N+F = HARTE Sperre (kein Override), sonst Warnung mit Bestätigung.
            if (e.response?.status === 409 && d?.code === "warranty_supplier_nf") {
                const list = (d.conflicts || []).map((c) =>
                    `• ${c.sku ? c.sku + " — " : ""}${c.article_name || "Artikel"} → Hersteller: ${c.manufacturer}${c.work_order_number ? ` (Auftrag ${c.work_order_number})` : ""}`
                ).join("\n");
                window.alert(`🚫 GARANTIE-TEILE — BESTELLUNG ÜBER NIEMANN+FREY GESPERRT!\n\n${list}\n\nGarantie-Teile müssen direkt beim Hersteller bestellt werden. Bitte den Hersteller als Lieferant wählen.`);
            } else if (e.response?.status === 409 && d?.code === "warranty_supplier") {
                const list = (d.conflicts || []).map((c) =>
                    `• ${c.sku ? c.sku + " — " : ""}${c.article_name || "Artikel"} → Hersteller: ${c.manufacturer}${c.work_order_number ? ` (Auftrag ${c.work_order_number})` : ""}`
                ).join("\n");
                const ok = window.confirm(
                    `⚠️ GARANTIE-TEILE MÜSSEN BEIM HERSTELLER BESTELLT WERDEN!\n\n${list}\n\nDer gewählte Lieferant ist NICHT der Hersteller. Bei Fremdbezug kann der Garantieanspruch verfallen.\n\nTrotzdem bei diesem Lieferanten bestellen?`
                );
                if (ok) {
                    try {
                        const payload2 = { ...payload, warranty_override: true };
                        await api.post("/purchase-orders", payload2);
                        toast.success("Bestellung angelegt (Garantie-Hinweis bestätigt)");
                        onSaved();
                    } catch (e2) { toast.error(String(e2.response?.data?.detail || "Fehler")); }
                }
            } else {
                toast.error(typeof d === "string" ? d : "Fehler");
            }
        }
        setBusy(false);
    };

    return (
        <Modal onClose={onClose} title={isEdit ? `Bestellung ${prefill.order_number}` : "Neue Bestellung"}>
            <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Lieferant *</label>
                        <Select value={supplierId} onValueChange={setSupplierId}>
                            <SelectTrigger className="rounded-sm mt-1" data-testid="po-supplier"><SelectValue placeholder="Lieferant wählen…" /></SelectTrigger>
                            <SelectContent>
                                {suppliers.filter((s) => s.is_active).map((s) => (
                                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Liefertermin (Soll)</label>
                        <Input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} className="rounded-sm mt-1" />
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Positionen</label>
                        <Button type="button" size="sm" variant="outline" onClick={addItem} className="h-7 text-[11px] rounded-sm" data-testid="po-add-item">
                            <Plus className="w-3 h-3 mr-1" /> Position
                        </Button>
                    </div>
                    <div className="space-y-2">
                        {items.map((it, i) => (
                            <div key={i} className="bg-gray-50 border border-gray-200 rounded-sm p-2.5" data-testid={`po-item-${i}`}>
                                <div className="flex gap-2 items-start mb-2">
                                    <Select value={it.article_id || "none"} onValueChange={(v) => v !== "none" && linkArticle(i, v)}>
                                        <SelectTrigger className="rounded-sm h-8 text-[12px] flex-1" data-testid={`po-item-art-${i}`}>
                                            <SelectValue placeholder="Artikel wählen…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">— wählen —</SelectItem>
                                            {articles.filter((a) => a.is_active).map((a) => (
                                                <SelectItem key={a.id} value={a.id}>{a.sku} · {a.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button type="button" size="sm" variant="outline" onClick={() => removeItem(i)} className="rounded-sm shrink-0" data-testid={`po-item-del-${i}`}>
                                        <X className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                                {it.article_id && (
                                    <div className="text-[11px] text-gray-600 mb-1.5 font-mono-num">{it.sku} · {it.name}</div>
                                )}
                                <div className="grid grid-cols-4 gap-2">
                                    <div>
                                        <label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Menge</label>
                                        <Input type="number" min="0" step="0.5" value={it.quantity || 0} onChange={(e) => setItem(i, "quantity", e.target.value)} className="rounded-sm h-8 text-sm" data-testid={`po-item-qty-${i}`} />
                                    </div>
                                    <div>
                                        <label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">EP netto €</label>
                                        <Input type="number" min="0" step="0.01" value={it.unit_price_net || 0} onChange={(e) => setItem(i, "unit_price_net", e.target.value)} className="rounded-sm h-8 text-sm" data-testid={`po-item-price-${i}`} />
                                    </div>
                                    <div>
                                        <label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Rabatt %</label>
                                        <Input type="number" min="0" max="80" value={it.discount_percent || 0} onChange={(e) => setItem(i, "discount_percent", e.target.value)} className="rounded-sm h-8 text-sm" />
                                    </div>
                                    <div>
                                        <label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">USt %</label>
                                        <Select value={String(it.vat_rate || 19)} onValueChange={(v) => setItem(i, "vat_rate", v)}>
                                            <SelectTrigger className="rounded-sm h-8 text-sm"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="19">19</SelectItem>
                                                <SelectItem value="7">7</SelectItem>
                                                <SelectItem value="0">0</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <Textarea placeholder="Hinweise (z. B. Eilbestellung, Lieferadresse abweichend)" value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm min-h-[50px]" />

                <div className="bg-[#111827] text-white rounded-sm p-3">
                    <div className="flex justify-between text-xs mb-1"><span className="uppercase tracking-wider text-gray-400">Netto</span><span className="font-mono-num">{fmtEur(totals.net)}</span></div>
                    <div className="flex justify-between text-xs mb-2"><span className="uppercase tracking-wider text-gray-400">USt</span><span className="font-mono-num">{fmtEur(totals.vat)}</span></div>
                    <div className="border-t border-gray-700 pt-2 flex justify-between items-baseline"><span className="text-xs uppercase tracking-wider font-bold">Brutto</span><span className="font-mono-num font-black text-2xl">{fmtEur(gross)}</span></div>
                </div>

                <Button type="submit" disabled={busy} className="w-full bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider" data-testid="po-submit">
                    {busy ? "Speichere…" : (isEdit ? "Aktualisieren" : "Bestellung anlegen")}
                </Button>
            </form>
        </Modal>
    );
}

function ReceiveModal({ order, onClose, onReceived }) {
    const [items, setItems] = useState(
        (order.items || []).map((it) => ({
            ...it,
            // outstanding (noch offen)
            outstanding: Math.max(0, Number(it.quantity || 0) - Number(it.received_qty || 0)),
            // diesmal liefern (default = outstanding)
            now_qty: Math.max(0, Number(it.quantity || 0) - Number(it.received_qty || 0)),
        })),
    );
    const [deliveryNote, setDeliveryNote] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const setQty = (i, v) => {
        const n = Math.max(0, Number(v) || 0);
        setItems((arr) => arr.map((x, idx) => idx === i ? {
            ...x, now_qty: Math.min(n, x.outstanding),
        } : x));
    };

    const totalNow = items.reduce((s, x) => s + (Number(x.now_qty) || 0), 0);
    const hasAny = items.some((x) => Number(x.now_qty) > 0);

    const submit = async (e) => {
        e.preventDefault();
        if (!hasAny) { toast.error("Mindestens eine Position mit Menge > 0"); return; }
        setBusy(true);
        try {
            const payload = {
                items: items.filter((x) => Number(x.now_qty) > 0).map((x) => ({
                    article_id: x.article_id, received_qty: Number(x.now_qty),
                })),
                delivery_note: deliveryNote || null,
                note: note || null,
            };
            const res = await api.post(`/purchase-orders/${order.id}/receive`, payload);
            toast.success(`Wareneingang gebucht · ${res.data.booked} Pos. · Status ${STATUS_META[res.data.status]?.label || res.data.status}`);
            onReceived();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
        setBusy(false);
    };

    return (
        <Modal onClose={onClose} title={`Wareneingang ${order.order_number}`}>
            <form onSubmit={submit} className="space-y-3">
                <div className="bg-green-50 border-l-4 border-green-500 p-3 text-xs text-green-900 rounded-sm">
                    <div className="flex gap-2 items-start">
                        <ClipboardCheck className="w-4 h-4 shrink-0 mt-0.5" />
                        <div>
                            Tragen Sie die <strong>tatsächlich gelieferten</strong> Mengen ein. Lagerbestände werden automatisch erhöht (StockMovement type='in'). Teil-Lieferungen sind erlaubt — Status bleibt bei „Teil-Eingang" bis alle Positionen vollständig sind.
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                    <Input value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} placeholder="Lieferschein-Nr." className="rounded-sm" data-testid="recv-delivery-note" />
                    <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notiz (optional)" className="rounded-sm" />
                </div>

                <div className="border border-gray-200 rounded-sm divide-y divide-gray-100 max-h-[50vh] overflow-y-auto" data-testid="recv-items">
                    {items.map((it, i) => {
                        const ordered = Number(it.quantity || 0);
                        const already = Number(it.received_qty || 0);
                        const willBe = already + Number(it.now_qty || 0);
                        const complete = willBe >= ordered;
                        return (
                            <div key={i} className="p-3 flex gap-3 items-center" data-testid={`recv-item-${i}`}>
                                <div className="min-w-0 flex-1">
                                    <div className="font-mono-num text-[10px] text-gray-500">{it.sku}</div>
                                    <div className="font-bold text-sm truncate">{it.name}</div>
                                    <div className="text-[11px] text-gray-600">
                                        Bestellt {fmtNum(ordered)} {it.unit || "Stück"} · bereits erhalten {fmtNum(already)} · offen <strong>{fmtNum(it.outstanding)}</strong>
                                    </div>
                                </div>
                                <div className="shrink-0 text-right">
                                    <label className="text-[9px] uppercase tracking-wider text-gray-500 font-bold block">Diesmal</label>
                                    <Input
                                        type="number"
                                        min="0"
                                        max={it.outstanding}
                                        step="0.5"
                                        value={it.now_qty}
                                        onChange={(e) => setQty(i, e.target.value)}
                                        className={`w-20 rounded-sm h-8 text-sm text-center font-mono-num ${complete && Number(it.now_qty) > 0 ? "border-green-500 bg-green-50" : ""}`}
                                        disabled={it.outstanding === 0}
                                        data-testid={`recv-qty-${i}`}
                                    />
                                    {it.outstanding === 0 && <div className="text-[9px] text-green-700 mt-0.5 inline-flex items-center"><Check className="w-3 h-3 mr-0.5" /> Komplett</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="text-xs text-gray-600 text-right">Diesmal gesamt: <strong className="font-mono-num">{fmtNum(totalNow)}</strong></div>

                <Button type="submit" disabled={busy || !hasAny} className="w-full bg-green-600 hover:bg-green-700 text-white font-black rounded-sm uppercase tracking-wider disabled:opacity-50" data-testid="recv-submit">
                    {busy ? "Buche…" : "Wareneingang buchen"}
                </Button>
            </form>
        </Modal>
    );
}

function SendOrderModal({ order, supplier, onClose, onSend }) {
    const [toEmail, setToEmail] = useState(supplier?.email || "");
    const [ccSelf, setCcSelf] = useState(true);
    const [message, setMessage] = useState("");
    const [skipEmail, setSkipEmail] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!skipEmail && !toEmail.trim()) {
            toast.error('E-Mail-Adresse erforderlich oder "Nur Status setzen" wählen');
            return;
        }
        setBusy(true);
        await onSend({
            to_email: toEmail.trim() || null,
            cc_self: ccSelf,
            message: message.trim() || null,
            skip_email: skipEmail,
        });
        setBusy(false);
    };

    return (
        <Modal onClose={onClose} title={`Bestellung ${order.order_number} versenden`}>
            <form onSubmit={submit} className="space-y-4">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-3 text-xs text-blue-900 rounded-sm">
                    <div className="flex gap-2 items-start">
                        <Send className="w-4 h-4 shrink-0 mt-0.5" />
                        <div>
                            Die Bestellung wird mit dem PDF-Anhang per E-Mail an den Lieferanten versendet. Status wechselt automatisch auf <strong>"Versendet"</strong>. Diese Aktion wird im Audit-Trail dokumentiert.
                        </div>
                    </div>
                </div>

                <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-700">E-Mail an Lieferant</label>
                    <Input
                        type="email"
                        value={toEmail}
                        onChange={(e) => setToEmail(e.target.value)}
                        placeholder={supplier?.email || "bestellung@lieferant.de"}
                        disabled={skipEmail}
                        className="rounded-sm mt-1 disabled:opacity-50"
                        data-testid="send-to-email"
                    />
                    {supplier?.email && supplier.email === toEmail && (
                        <div className="text-[10px] text-gray-500 mt-1">Aus Lieferanten-Stammdaten übernommen</div>
                    )}
                </div>

                <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Nachricht (optional)</label>
                    <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="z. B. Bitte um Eilbearbeitung · Lieferadresse beachten"
                        disabled={skipEmail}
                        className="rounded-sm mt-1 min-h-[70px] disabled:opacity-50"
                        data-testid="send-message"
                    />
                </div>

                <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={ccSelf} onChange={(e) => setCcSelf(e.target.checked)} disabled={skipEmail} data-testid="send-cc-self" />
                    Kopie an meine eigene E-Mail-Adresse (CC)
                </label>

                <label className="flex items-center gap-2 text-sm cursor-pointer pt-2 border-t border-gray-100">
                    <input type="checkbox" checked={skipEmail} onChange={(e) => setSkipEmail(e.target.checked)} data-testid="send-skip-email" />
                    Nur Status setzen — kein Mail-Versand (z. B. wenn bereits telefonisch/fax bestellt)
                </label>

                <Button
                    type="submit"
                    disabled={busy}
                    className="w-full rounded-sm bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black uppercase tracking-wider"
                    data-testid="send-submit"
                >
                    <Send className="w-4 h-4 mr-1.5" />
                    {busy ? "Versende…" : (skipEmail ? "Status auf Versendet setzen" : "Versenden")}
                </Button>
            </form>
        </Modal>
    );
}

function Modal({ children, onClose, title }) {
    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-sm w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                    <h2 className="font-display font-black text-xl">{title}</h2>
                    <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100" data-testid="modal-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}
